import { defineComponent as _defineComponent } from 'vue';
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { Search, XCircle, Check, Loader2 } from 'lucide-vue-next';
export default /*@__PURE__*/ _defineComponent({
    __name: 'Combobox',
    props: {
        modelValue: { type: [String, Number, null, Array], required: false },
        options: { type: Array, required: true },
        placeholder: { type: String, required: false, default: 'Select an option...' },
        disabled: { type: Boolean, required: false, default: false },
        loading: { type: Boolean, required: false, default: false },
        loadingText: { type: String, required: false, default: 'Loading options...' },
        multiple: { type: Boolean, required: false, default: false },
        id: { type: String, required: false, default: undefined },
        ariaLabel: { type: String, required: false, default: undefined },
        maxRenderCount: { type: Number, required: false, default: 100 },
        dataTrack: { type: String, required: false },
        dataDetail: { type: String, required: false }
    },
    emits: ["update:modelValue", "change"],
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        let _uid = 0;
        // ------------------------------------------------------------------
        // PROPS & EMITS
        // ------------------------------------------------------------------
        const props = __props;
        const emit = __emit;
        // ------------------------------------------------------------------
        // STATE & ACCESSIBILITY IDS
        // ------------------------------------------------------------------
        const uid = `combobox-${++_uid}`;
        const listboxId = `${uid}-listbox`;
        const getOptionId = (idx) => `${uid}-option-${idx}`;
        const isOpen = ref(false);
        const searchQuery = ref('');
        const highlightedIndex = ref(-1);
        const isFocused = ref(false);
        const containerRef = ref(null);
        const triggerButtonRef = ref(null);
        const searchInputRef = ref(null);
        // ------------------------------------------------------------------
        // COMPUTED & LOOKUP HELPERS
        // ------------------------------------------------------------------
        const isMultiSelect = computed(() => props.multiple && Array.isArray(props.modelValue));
        // O(1) Set lookup for fast checks across thousands of options
        const selectedValueSet = computed(() => {
            if (props.modelValue == null)
                return new Set();
            if (isMultiSelect.value && Array.isArray(props.modelValue)) {
                return new Set(props.modelValue.map(String));
            }
            return new Set([String(props.modelValue)]);
        });
        const isOptionSelected = (val) => {
            return selectedValueSet.value.has(String(val));
        };
        const selectedOptions = computed(() => {
            if (props.modelValue == null)
                return [];
            return props.options.filter((opt) => selectedValueSet.value.has(String(opt.value)));
        });
        const displayText = computed(() => {
            if (selectedOptions.value.length === 0)
                return props.placeholder;
            if (selectedOptions.value.length === 1)
                return selectedOptions.value[0].label;
            return `${selectedOptions.value.length} Selected`;
        });
        // Full filtered list (retains original positions in the bottom section)
        const filteredOptions = computed(() => {
            if (props.loading)
                return [];
            let list = props.options;
            if (searchQuery.value.trim()) {
                const query = searchQuery.value.toLowerCase();
                list = list.filter((opt) => opt.label.toLowerCase().includes(query));
            }
            // Limit rendered items in the DOM for high performance with thousands of items
            return list.slice(0, props.maxRenderCount);
        });
        const activeDescendant = computed(() => isOpen.value && highlightedIndex.value >= 0
            ? getOptionId(highlightedIndex.value)
            : undefined);
        // ------------------------------------------------------------------
        // ACTIONS & KEYBOARD NAVIGATION
        // ------------------------------------------------------------------
        const focusTrigger = () => {
            nextTick(() => triggerButtonRef.value?.focus());
        };
        const getFirstEnabledIndex = (fromIdx = 0, step = 1) => {
            const len = filteredOptions.value.length;
            if (len === 0)
                return -1;
            let curr = (fromIdx + len) % len;
            for (let i = 0; i < len; i++) {
                if (!filteredOptions.value[curr]?.disabled) {
                    return curr;
                }
                curr = (curr + step + len) % len;
            }
            return -1;
        };
        const openDropdown = () => {
            if (props.disabled)
                return;
            isOpen.value = true;
            searchQuery.value = '';
            const selectedIdx = filteredOptions.value.findIndex((opt) => isOptionSelected(opt.value));
            const initialIdx = selectedIdx !== -1 ? selectedIdx : 0;
            highlightedIndex.value = filteredOptions.value[initialIdx]?.disabled
                ? getFirstEnabledIndex(initialIdx, 1)
                : initialIdx;
            nextTick(() => searchInputRef.value?.focus());
        };
        const closeDropdown = (restoreFocus = true) => {
            if (!isOpen.value)
                return;
            isOpen.value = false;
            searchQuery.value = '';
            highlightedIndex.value = -1;
            isFocused.value = false;
            if (restoreFocus) {
                focusTrigger();
            }
        };
        const toggleDropdown = () => {
            if (props.disabled)
                return;
            isOpen.value ? closeDropdown(true) : openDropdown();
        };
        const selectOption = (option) => {
            if (option.disabled || props.loading)
                return;
            if (isMultiSelect.value) {
                const currentValues = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
                const index = currentValues.findIndex((item) => String(item) === String(option.value));
                if (index > -1) {
                    currentValues.splice(index, 1);
                }
                else {
                    currentValues.push(option.value);
                }
                const selectedList = props.options.filter((opt) => currentValues.some((val) => String(val) === String(opt.value)));
                emit('update:modelValue', currentValues);
                emit('change', selectedList);
            }
            else {
                emit('update:modelValue', option.value);
                emit('change', option);
                closeDropdown(true);
            }
        };
        const clearSelection = (e) => {
            e.stopPropagation();
            const emptyVal = isMultiSelect.value ? [] : null;
            emit('update:modelValue', emptyVal);
            emit('change', emptyVal);
            focusTrigger();
        };
        const scrollToHighlighted = () => {
            nextTick(() => {
                const listEl = containerRef.value?.querySelector('.select-search-options');
                const itemEl = listEl?.querySelectorAll('.options-list-section .select-search-item')[highlightedIndex.value];
                if (listEl && itemEl) {
                    const listTop = listEl.scrollTop;
                    const listBottom = listTop + listEl.clientHeight;
                    const itemTop = itemEl.offsetTop;
                    const itemBottom = itemTop + itemEl.clientHeight;
                    if (itemTop < listTop) {
                        listEl.scrollTop = itemTop;
                    }
                    else if (itemBottom > listBottom) {
                        listEl.scrollTop = itemBottom - listEl.clientHeight;
                    }
                }
            });
        };
        const handleKeyDown = (e) => {
            if (!isOpen.value) {
                if (['Enter', 'ArrowDown', 'ArrowUp', ' '].includes(e.key)) {
                    e.preventDefault();
                    openDropdown();
                }
                return;
            }
            if (props.loading)
                return;
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    if (filteredOptions.value.length > 0) {
                        highlightedIndex.value = getFirstEnabledIndex(highlightedIndex.value + 1, 1);
                        scrollToHighlighted();
                    }
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    if (filteredOptions.value.length > 0) {
                        highlightedIndex.value = getFirstEnabledIndex(highlightedIndex.value - 1, -1);
                        scrollToHighlighted();
                    }
                    break;
                case 'Home':
                    e.preventDefault();
                    if (filteredOptions.value.length > 0) {
                        highlightedIndex.value = getFirstEnabledIndex(0, 1);
                        scrollToHighlighted();
                    }
                    break;
                case 'End':
                    e.preventDefault();
                    if (filteredOptions.value.length > 0) {
                        highlightedIndex.value = getFirstEnabledIndex(filteredOptions.value.length - 1, -1);
                        scrollToHighlighted();
                    }
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (highlightedIndex.value >= 0 &&
                        highlightedIndex.value < filteredOptions.value.length) {
                        selectOption(filteredOptions.value[highlightedIndex.value]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    closeDropdown(true);
                    break;
                case 'Tab':
                    closeDropdown(false);
                    break;
            }
        };
        const handleClickOutside = (e) => {
            if (containerRef.value && !containerRef.value.contains(e.target)) {
                closeDropdown(false);
            }
        };
        onMounted(() => document.addEventListener('click', handleClickOutside));
        onBeforeUnmount(() => document.removeEventListener('click', handleClickOutside));
        watch(searchQuery, () => {
            highlightedIndex.value = getFirstEnabledIndex(0, 1);
        });
        const __returned__ = { get _uid() { return _uid; }, set _uid(v) { _uid = v; }, props, emit, uid, listboxId, getOptionId, isOpen, searchQuery, highlightedIndex, isFocused, containerRef, triggerButtonRef, searchInputRef, isMultiSelect, selectedValueSet, isOptionSelected, selectedOptions, displayText, filteredOptions, activeDescendant, focusTrigger, getFirstEnabledIndex, openDropdown, closeDropdown, toggleDropdown, selectOption, clearSelection, scrollToHighlighted, handleKeyDown, handleClickOutside, get Search() { return Search; }, get XCircle() { return XCircle; }, get Check() { return Check; }, get Loader2() { return Loader2; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
